import type React from "react";
import { ActionIcon, Group, Tooltip } from "@mantine/core";
import { Table } from "@xplatform/design-system";
import {
  IconCheck,
  IconHistory,
  IconRotateClockwise,
  IconX,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

interface RowActionsCellProps {
  isSaving: boolean;
  actionsDisabled: boolean;
  hasTopMatch: boolean;
  /** Drives the reject/recover swap: an already-rejected row offers the way
   *  back rather than a second reject. */
  isRejected: boolean;
  onAccept: () => void;
  onReject: () => void;
  onOpenHistory: () => void;
  onRecover: () => void;
}

/**
 * The Actions column: accept, reject (or recover), history. Three buttons and
 * nothing else.
 *
 * It used to open with a chevron for the row's alternatives, and to close with
 * an overflow menu. Both are gone. The chevron read as a fourth ACTION sitting
 * among three real ones rather than as a disclosure — and what it suggested,
 * next to a menu, was "this opens a dropdown". wd shows the one suggestion the
 * pipeline settled on, so there was no list behind it worth that confusion.
 */
const RowActionsCell: React.FC<RowActionsCellProps> = ({
  isSaving,
  actionsDisabled,
  hasTopMatch,
  isRejected,
  onAccept,
  onReject,
  onOpenHistory,
  onRecover,
}) => {
  const { t } = useTranslation();

  return (
    <Table.Td>
      <Group gap={4} wrap="nowrap" justify="flex-start">
        <Tooltip label={t("resultRow.actions.accept")}>
          <ActionIcon
            size="sm"
            variant="light"
            color="teal"
            onClick={onAccept}
            disabled={!hasTopMatch || actionsDisabled}
            loading={isSaving}
            aria-label={t("resultRow.actions.accept")}
          >
            <IconCheck size={14} />
          </ActionIcon>
        </Tooltip>
        {isRejected ? (
          <Tooltip
            label={t("resultRow.actions.recover", { defaultValue: "Recover" })}
          >
            <ActionIcon
              size="sm"
              variant="light"
              color="blue"
              onClick={onRecover}
              disabled={actionsDisabled}
              loading={isSaving}
              aria-label={t("resultRow.actions.recover", {
                defaultValue: "Recover",
              })}
            >
              <IconRotateClockwise size={14} />
            </ActionIcon>
          </Tooltip>
        ) : (
          <Tooltip label={t("resultRow.actions.reject")}>
            <ActionIcon
              size="sm"
              variant="light"
              color="red"
              onClick={onReject}
              disabled={actionsDisabled}
              loading={isSaving}
              aria-label={t("resultRow.actions.reject")}
            >
              <IconX size={14} />
            </ActionIcon>
          </Tooltip>
        )}
        <Tooltip label={t("resultRow.actions.history")}>
          <ActionIcon
            size="sm"
            variant="subtle"
            color="blue"
            onClick={onOpenHistory}
            aria-label={t("resultRow.actions.history")}
          >
            <IconHistory size={14} />
          </ActionIcon>
        </Tooltip>
      </Group>
    </Table.Td>
  );
};

export default RowActionsCell;
